import React, { useEffect, useState } from "react";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { ArrowLeft, Shield } from "lucide-react";
import { CartItem, Address } from "../types";
import { useTranslation } from "react-i18next";
import { loadStripe } from "@stripe/stripe-js";
import { createPaymentIntent } from "../api/order";
import { toast } from "sonner";
import { removeItem } from "../utils/localStorage";

// It's best to load Stripe outside of a component's render to avoid
// recreating the Stripe object on every render.
// This line should be configured with your actual publishable key
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

interface CheckoutProps {
  cartItems: CartItem[];
  totalPrice: number;
  deliveryOption: "delivery" | "pickup";
  deliveryAddress?: Address;
  deliveryFee: number;
  onOrderPlaced: (order: Order) => void;
  onBack: () => void;
  orderId: string;
}

const CheckoutForm: React.FC<Omit<CheckoutProps, "onBack">> = ({
  cartItems,
  totalPrice,
  deliveryOption,
  deliveryAddress,
  deliveryFee,
  onOrderPlaced,
  orderId,
}) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    password: "",
    cardName: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [paymentStep, setPaymentStep] = useState<"info" | "payment">("payment");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const finalTotal = totalPrice + deliveryFee;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    if (paymentStep === "info") {
      setPaymentStep("payment");
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    const cardElement = elements.getElement(CardNumberElement);

    if (!cardElement) {
      setIsLoading(false);
      setErrorMessage("Card details not found.");
      return;
    }

    const paymentIntentResponse = await createPaymentIntent(orderId);

    if (paymentIntentResponse.error || !paymentIntentResponse.data) {
      setErrorMessage(
        paymentIntentResponse.error?.message || "Could not initiate payment."
      );
      toast.error(
        `Login failed: ${
          paymentIntentResponse.error?.message || "Could not initiate payment."
        }`
      );
      setIsLoading(false);
      return;
    }

    const { clientSecret } = paymentIntentResponse.data;

    const { error: paymentError, paymentIntent } =
      await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: formData.cardName,
          },
        },
      });

    if (paymentError) {
      setErrorMessage(
        paymentError.message || "An error occurred during payment."
      );
      setIsLoading(false);
      toast.error(
        `Login failed: ${paymentError.message || "Could not initiate payment."}`
      );
      return;
    }

    if (paymentIntent.status === "succeeded") {
      const order: Order = {
        id: orderId,
        items: cartItems,
        total: finalTotal,
        status: "PREPARING",
        estimatedTime: deliveryOption === "pickup" ? 20 : 30,
        customerInfo: {
          name: formData.name || "Client Invité",
          phone: formData.phone || "+237 6XX XXX XXX",
          address:
            deliveryOption === "delivery" && deliveryAddress
              ? deliveryAddress
              : {
                  street: "N/A",
                  city: "N/A",
                  country: "N/A",
                  zipCode: "N/A",
                  latitude: 0,
                  longitude: 0,
                  fullAddress: "Retrait sur place",
                },
        },
        createdAt: new Date().toISOString(),
      };
      onOrderPlaced(order);
      removeItem("currentOrderID");
    } else {
      setErrorMessage(`Payment failed with status: ${paymentIntent.status}`);
    }

    setIsLoading(false);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const cardElementOptions = {
    style: {
      base: {
        color: "#32325d",
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": {
          color: "#aab7c4",
        },
      },
      invalid: {
        color: "#fa755a",
        iconColor: "#fa755a",
      },
    },
    classes: {
      base: "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent",
    },
  };

  return (
    <div className="lg:col-span-2">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-center mb-6">
            <Shield className="w-6 h-6 text-green-500 mr-3" />
            <h2 className="text-xl font-semibold text-black">
              {t("checkout.securePaymentByStripe")}
            </h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("checkout.nameOnCard")}
              </label>
              <input
                type="text"
                name="cardName"
                value={formData.cardName}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder={t("checkout.nameOnCardPlaceholder")}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("checkout.cardNumber")}
              </label>
              <CardNumberElement options={cardElementOptions} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("checkout.expirationDate")}
                </label>
                <CardExpiryElement options={cardElementOptions} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("checkout.cvv")}
                </label>
                <CardCvcElement options={cardElementOptions} />
              </div>
            </div>
          </div>

          {errorMessage && (
            <div className="mt-4 text-red-600 text-sm">{errorMessage}</div>
          )}

          <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center text-green-800">
              <Shield className="w-5 h-5 mr-2" />
              <span className="text-sm font-medium">
                {t("checkout.paymentInfoSecure")}
              </span>
            </div>
          </div>
        </div>
        <button
          type="submit"
          disabled={isLoading || !stripe}
          className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-200 ${
            isLoading || !stripe
              ? "bg-gray-400 text-gray-200 cursor-not-allowed"
              : "bg-orange-500 text-white hover:bg-orange-600 shadow-lg hover:shadow-xl"
          }`}
        >
          {isLoading ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>{t("checkout.processingPayment")}</span>
            </div>
          ) : paymentStep === "info" ? (
            t("checkout.continueToPayment")
          ) : (
            `${t("checkout.pay")} ${finalTotal.toFixed(2)} CAD`
          )}
        </button>
      </form>
    </div>
  );
};

const Checkout: React.FC<CheckoutProps> = (props) => {
  const { t } = useTranslation();
  const { onBack, cartItems, totalPrice, deliveryOption, deliveryFee } = props;
  const [paymentStep, setPaymentStep] = useState<"info" | "payment">("payment");
  const finalTotal = totalPrice + deliveryFee;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center mb-8">
        <button
          onClick={onBack}
          className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-3xl font-bold text-black">
          {paymentStep === "info"
            ? t("checkout.orderInformation")
            : t("checkout.securePayment")}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <Elements stripe={stripePromise}>
          <CheckoutForm {...props} />
        </Elements>

        {/* Résumé de commande */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-gray-100 p-6 sticky top-8">
            <h3 className="text-lg font-semibold text-black mb-4">
              {t("checkout.orderSummary")}
            </h3>

            {/* Articles */}
            <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
              {cartItems.map((item) => (
                <div key={item.id} className="flex items-center space-x-3">
                  <img
                    src={item.dish.imageUrl}
                    alt={item.dish.name}
                    className="w-12 h-12 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h4 className="font-medium text-sm">{item.dish.name}</h4>
                    <p className="text-xs text-gray-500">
                      {t("checkout.qty")} {item.quantity}
                    </p>
                  </div>
                  <span className="text-sm font-medium">
                    {(item.dish.price * item.quantity).toLocaleString()} CAD
                  </span>
                </div>
              ))}
            </div>

            {/* Totaux */}
            <div className="border-t border-gray-200 pt-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">{t("checkout.subtotal")}</span>
                <span className="font-medium">
                  ${totalPrice.toFixed(2)} CAD
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">
                  {deliveryOption === "delivery"
                    ? t("checkout.delivery")
                    : t("checkout.pickup")}
                </span>
                <span className="font-medium">
                  {deliveryOption === "delivery"
                    ? `${deliveryFee.toFixed(2)} CAD`
                    : t("checkout.free")}
                </span>
              </div>
              <div className="border-t border-gray-200 pt-2">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-black">
                    {t("checkout.total")}
                  </span>
                  <span className="text-xl font-bold text-orange-500">
                    ${finalTotal.toFixed(2)} CAD
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-4 text-xs text-gray-500">
              <p>{t("checkout.secureSSL")}</p>
              <p>{t("checkout.dataProtected")}</p>
              <p>{t("checkout.satisfactionGuaranteed")}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
